import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, TrendingUp, TrendingDown, Target, Sparkles,
  Calendar, DollarSign, Package, RefreshCw, Lightbulb,
  BarChart3, Activity, AlertCircle, CheckCircle2, ArrowUp, ArrowDown
} from 'lucide-react';
import { api } from '@/lib/api';

interface Prediction {
  type: string;
  targetDate: string;
  predictedValue: number;
  confidence: number;
  parameters: any;
}

interface DailyTrend {
  date: string;
  shipments: number;
  delivered: number;
  revenue: number;
}

interface TrendData {
  dailyTrend: DailyTrend[];
  weeklyTrend: any[];
  statusDistribution: { status: string; count: number }[];
  period: string;
}

export default function AnalyticsPage() {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [trends, setTrends] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [predictionsRes, trendsRes] = await Promise.all([
        api.get(`/api/analytics/predictions?days=${days}`),
        api.get('/api/analytics/trends')
      ]);

      if (predictionsRes.data.success) setPredictions(predictionsRes.data.data);
      if (trendsRes.data.success) setTrends(trendsRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const shipmentPredictions = predictions.filter(p => p.type === 'shipments');
  const revenuePredictions = predictions.filter(p => p.type === 'revenue');

  const avgDailyShipments = trends?.dailyTrend.length 
    ? trends.dailyTrend.reduce((sum, d) => sum + d.shipments, 0) / trends.dailyTrend.length 
    : 0;

  const avgDailyRevenue = trends?.dailyTrend.length
    ? trends.dailyTrend.reduce((sum, d) => sum + d.revenue, 0) / trends.dailyTrend.length
    : 0;

  const totalDelivered = trends?.dailyTrend.reduce((sum, d) => sum + d.delivered, 0) || 0;
  const totalShipments = trends?.dailyTrend.reduce((sum, d) => sum + d.shipments, 0) || 0;
  const deliveryRate = totalShipments > 0 ? (totalDelivered / totalShipments) * 100 : 0;

  const maxShipments = Math.max(...(trends?.dailyTrend.map(d => d.shipments) || [1]), 1);
  const maxRevenue = Math.max(...(trends?.dailyTrend.map(d => d.revenue) || [1]), 1);

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 85) return 'text-green-600 bg-green-100';
    if (confidence >= 70) return 'text-blue-600 bg-blue-100';
    if (confidence >= 55) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            التحليلات الذكية
          </h1>
          <p className="text-muted-foreground mt-1">تحليلات متقدمة وتنبؤات ذكية لأداء النظام</p>
        </div>
        <div className="flex gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-4 py-2 border rounded-lg bg-white"
          >
            <option value="3">3 أيام</option>
            <option value="7">7 أيام</option>
            <option value="14">14 يوم</option>
            <option value="30">30 يوم</option>
          </select>
          <Button onClick={loadData} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ml-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">متوسط الشحنات/يوم</p>
                <p className="text-2xl font-bold text-blue-900">{avgDailyShipments.toFixed(1)}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">متوسط الإيرادات/يوم</p>
                <p className="text-2xl font-bold text-green-900">{avgDailyRevenue.toFixed(0)} ج.م</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">معدل التسليم</p>
                <p className="text-2xl font-bold text-purple-900">{deliveryRate.toFixed(1)}%</p>
              </div>
              <Target className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">إجمالي الشحنات</p>
                <p className="text-2xl font-bold text-amber-900">{totalShipments}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Insights Banner */}
      <Card className="bg-gradient-to-l from-purple-600 via-indigo-600 to-blue-600 text-white border-0 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full -translate-x-48 -translate-y-48 blur-3xl"></div>
        <CardContent className="p-6 relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold">رؤى ذكاء اصطناعي</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-yellow-300" />
                <p className="text-sm text-white/80">توصية</p>
              </div>
              <p className="text-sm">
                {deliveryRate < 70 
                  ? 'معدل التسليم منخفض. ينصح بمراجعة عمليات التوزيع.'
                  : 'معدل التسليم جيد. حافظ على هذا الأداء!'}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-300" />
                <p className="text-sm text-white/80">اتجاه</p>
              </div>
              <p className="text-sm">
                متوسط الشحنات اليومي: <strong>{avgDailyShipments.toFixed(1)}</strong>
                <br />مع اتجاه مستقر
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-blue-300" />
                <p className="text-sm text-white/80">هدف</p>
              </div>
              <p className="text-sm">
                الهدف المقترح: زيادة الشحنات بنسبة 15% خلال 30 يوم
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Shipment Predictions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              تنبؤات الشحنات
            </CardTitle>
          </CardHeader>
          <CardContent>
            {shipmentPredictions.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">لا توجد تنبؤات</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shipmentPredictions.map((pred, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="font-semibold">{new Date(pred.targetDate).toLocaleDateString('ar-EG')}</p>
                        <p className="text-xs text-gray-500">ثقة: {pred.confidence}%</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="text-lg font-bold text-blue-600">{pred.predictedValue.toFixed(1)}</p>
                      <p className="text-xs text-gray-500">شحنة متوقعة</p>
                    </div>
                    <Badge className={getConfidenceColor(pred.confidence)}>
                      {pred.confidence}%
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Predictions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              تنبؤات الإيرادات
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenuePredictions.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">لا توجد تنبؤات</p>
              </div>
            ) : (
              <div className="space-y-3">
                {revenuePredictions.map((pred, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="font-semibold">{new Date(pred.targetDate).toLocaleDateString('ar-EG')}</p>
                        <p className="text-xs text-gray-500">ثقة: {pred.confidence}%</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="text-lg font-bold text-green-600">{pred.predictedValue.toFixed(0)} ج.م</p>
                      <p className="text-xs text-gray-500">إيراد متوقع</p>
                    </div>
                    <Badge className={getConfidenceColor(pred.confidence)}>
                      {pred.confidence}%
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Trends Chart */}
      {trends && trends.dailyTrend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-500" />
              الاتجاهات اليومية
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Shipments Chart */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-500" />
                    الشحنات
                  </h3>
                  <span className="text-sm text-gray-500">آخر {trends.dailyTrend.length} يوم</span>
                </div>
                <div className="flex items-end gap-1 h-32">
                  {trends.dailyTrend.map((day, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                      <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                        <div 
                          className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all hover:from-blue-600 hover:to-blue-500 relative"
                          style={{ height: `${(day.shipments / maxShipments) * 100}%`, minHeight: day.shipments > 0 ? '4px' : '0' }}
                        >
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                            {day.shipments}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 rotate-45 origin-top-left">
                        {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Revenue Chart */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    الإيرادات
                  </h3>
                  <span className="text-sm text-gray-500">آخر {trends.dailyTrend.length} يوم</span>
                </div>
                <div className="flex items-end gap-1 h-32">
                  {trends.dailyTrend.map((day, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                      <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                        <div 
                          className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t transition-all hover:from-green-600 hover:to-green-500 relative"
                          style={{ height: `${(day.revenue / maxRevenue) * 100}%`, minHeight: day.revenue > 0 ? '4px' : '0' }}
                        >
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                            {day.revenue.toFixed(0)} ج.م
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 rotate-45 origin-top-left">
                        {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Distribution */}
      {trends && trends.statusDistribution.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-orange-500" />
              توزيع حالات الشحنات
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {trends.statusDistribution.map((status, i) => {
                const total = trends.statusDistribution.reduce((sum, s) => sum + s.count, 0);
                const percentage = total > 0 ? (status.count / total) * 100 : 0;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-32 text-sm font-medium">{status.status || 'غير محدد'}</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-end px-3 text-white text-sm font-semibold transition-all"
                        style={{ width: `${percentage}%` }}
                      >
                        {status.count}
                      </div>
                    </div>
                    <div className="w-16 text-sm text-gray-600 text-left">{percentage.toFixed(1)}%</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
